import Sidebar from "layouts/Main/components/Sidebar/Sidebar";
import theme from "theme";
import ScrollReset from "components/ScrollReset";
import { Outlet, useLocation } from "react-router-dom";
import { Box, styled } from "@mui/material";
import { Breadcrumb } from "components/Breadcrumb";
import ServerConnectivity from "components/ServerConnectivity";
import { PortalContext, usePortal } from "contexts/PortalContext";
import { cn } from "components/lib/utils";

const ContentGrid = styled(Box)(() => ({
  gridArea: "gridContent",
  display: "grid",
  gridTemplateRows: "minmax(0px, 1fr)",
  minHeight: 0,
  gap: "0px 0px",
  gridTemplateAreas: `"gutterLeft content gutterRight"`,
  height: "100%",
  [theme.breakpoints.only("xs")]: {
    gridTemplateColumns: "minmax(8px, auto) minmax(auto, 1168px) minmax(8px, auto)",
  },
}));

const GridLayout = () => {
  const location = useLocation();
  const paths = location.pathname.split("/");
  const breadcrumbPortal = usePortal();
  const potentiallyFullWidth = paths[1] === "lens" && paths[3] === "cases";
  const isDashboard = paths[1] === "dashboard";
  const isLensEditor = paths[1] === "lens-editor";
  const isLensEditorSettings = paths[1] === "lens-editor" && paths[3] === "settings";
  const shouldBeFullWidth =
    isDashboard ||
    (isLensEditor && !isLensEditorSettings) ||
    (paths[1] === "lens" && paths[3] === "report" && paths[4] !== undefined) ||
    (paths[1] === "lens" && paths[3] === "files") ||
    (potentiallyFullWidth && paths[5] === "read-quality") ||
    (potentiallyFullWidth && paths[5] === "profile") ||
    (potentiallyFullWidth && paths[5] === "review") ||
    (potentiallyFullWidth && paths[5] === "documents" && paths[6]);
  // if path is profile, we want noGutters
  const isProfile = potentiallyFullWidth && paths[5] === "profile";
  const isDBR = potentiallyFullWidth && paths[5] === "documents" && paths[6];
  const isImport = paths[1] === "import";
  const isUsers = paths[1] === "users";
  const hasNoTopNav = isUsers || isImport;

  return (
    <Box
      sx={{
        display: "grid",
        gridTemplateColumns: "64px 1fr",
        gridTemplateRows: "1fr",
        gap: "0px 0px",
        gridTemplateAreas: `"sidenav gridContent"`,
        height: "100%",
        overflowY: "auto",
        [theme.breakpoints.only("xs")]: {
          gridTemplateAreas: `
        "gridContent"
        "sidenav"
      `,
          gridTemplateColumns: "1fr",
          gridTemplateRows: "1fr 0fr",
          gap: "0px 8px",
        },
      }}
      id="grid-container">
      <PortalContext.Provider value={breadcrumbPortal}>
        <ServerConnectivity />
        <ScrollReset>
          <Sidebar />
          <Box
            sx={{
              height: "100%",
              display: "flex",
              minWidth: 0,
              minHeight: 0,
              width: "100%",
              flexDirection: "column",
            }}>
            <div className={cn("p-2 bg-white border-b border-gray-50 shadow-sm", hasNoTopNav ? "p-0" : "")}>
              <Breadcrumb portalRef={breadcrumbPortal.portalRef} />
            </div>
            <ContentGrid
              sx={
                shouldBeFullWidth
                  ? {
                      gridTemplateColumns: isProfile || isLensEditor || isDBR ? "0px auto 0px" : "24px auto 24px",
                    }
                  : {
                      gridTemplateColumns: "minmax(16px, auto) minmax(0px, 1270px) minmax(16px, auto)",
                    }
              }>
              <Box sx={{ gridArea: "gutterLeft" }}></Box>
              <main
                style={{
                  height: "100%",
                  display: "flex",
                  minWidth: 0,
                  width: "100%",
                  flexDirection: "column",
                  paddingBottom: isDBR || isProfile || isLensEditor ? undefined : theme.spacing(2),
                }}>
                <Outlet />
              </main>
              <Box
                sx={{
                  gridArea: "gutterRight",
                }}></Box>
            </ContentGrid>
          </Box>
        </ScrollReset>
      </PortalContext.Provider>
    </Box>
  );
};

export default GridLayout;
